import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Button } from '../ui/Button.js';
import { Modal } from '../ui/Modal.js';
import { Field } from '../ui/Field.js';
import { DateField } from '../ui/DateField.js';
import { HighlightedInput } from './HighlightedInput.js';
import { RepeatDropdown } from './RepeatDropdown.js';
import { parseQuickAdd } from '../lib/parser/index.js';
import { todayInParis } from '../lib/dates.js';
import { firstOccurrence } from '../lib/rrule-next.js';
import { useProjects } from '../stores/projects.js';
import { useTasks } from '../stores/tasks.js';
import { useToast } from '../stores/toast.js';
import { useUi } from '../stores/ui.js';
import { truncate } from '../lib/string.js';
import { texteBrut } from '../lib/linkify.js';
import { ApiError } from '../api/client.js';
import { errorMessage } from '../lib/errors.js';

function toErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 400) {
      const m = err.message.match(/too_big.*maximum[^,]*?(\d+)/i);
      if (m) return `Title too long (max ${m[1]} chars).`;
      return `Invalid input: ${err.message}`;
    }
    if (err.status === 401) return 'Session expired — please log in again.';
    if (err.status === 404) return 'Not found.';
    return err.message || 'Request failed.';
  }
  return errorMessage(err, 'Unknown error.');
}

export function QuickAddModal(): React.ReactElement | null {
  const open = useUi((s) => s.quickAddOpen);
  const defaultProjectId = useUi((s) => s.quickAddDefaultProjectId);
  const close = useUi((s) => s.closeQuickAdd);
  const openTaskDetail = useUi((s) => s.openTaskDetail);
  const projects = useProjects((s) => s.projects);
  const createTask = useTasks((s) => s.create);
  const showToast = useToast((s) => s.show);

  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [manualProjectId, setManualProjectId] = useState<string | null>(null);
  const [manualDueDate, setManualDueDate] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setValue('');
      setSubmitting(false);
      setError(null);
      setManualProjectId(null);
      setManualDueDate(null);
    }
  }, [open]);

  if (!open) return null;

  const today = todayInParis();
  const parsed = value ? parseQuickAdd(value, today) : null;

  const parsedProject =
    parsed?.projectName &&
    projects.find(
      (p) => p.name.toLowerCase() === parsed.projectName!.toLowerCase()
    );

  const resolvedProject =
    parsedProject ||
    projects.find((p) => p.projectId === manualProjectId) ||
    projects.find((p) => p.projectId === defaultProjectId) ||
    projects[0] ||
    null;

  const parsedDate =
    parsed?.dueDate ?? (parsed?.rrule ? firstOccurrence(parsed.rrule, today) : null);
  const resolvedDate = parsedDate ?? manualDueDate ?? (value ? today : null);

  async function submit(): Promise<void> {
    if (!value.trim() || submitting) return;
    if (!resolvedProject) {
      setError('Create a project first.');
      return;
    }
    setSubmitting(true);
    setError(null);
    const p = parseQuickAdd(value, today);
    const finalTitle = p.title || value.trim();
    const dueDate =
      p.dueDate ?? (p.rrule ? firstOccurrence(p.rrule, today) : null) ?? manualDueDate ?? today;
    try {
      const created = await createTask({
        title: finalTitle,
        projectId: resolvedProject.projectId,
        dueDate,
        rrule: p.rrule,
      });
      close();
      showToast({
        message: `Created · ${truncate(texteBrut(created.title), 40)}`,
        action: {
          label: 'Open',
          run: () => openTaskDetail(created.taskId),
        },
      });
    } catch (err) {
      setError(toErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="New task"
      anchor="top"
      debordant
      initialFocusRef={inputRef}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            onMouseDown={(e) => e.preventDefault()}
            disabled={!value.trim()}
            loading={submitting}
          >
            Add task
          </Button>
        </>
      }
    >
      <div className="pile">
        <Field label="Task" group>
          <HighlightedInput
            ref={inputRef}
            value={value}
            onChange={setValue}
            onKeyDown={onKeyDown}
            today={today}
            placeholder="e.g. Acheter du pain #perso demain every week"
            aria-label="Task"
          />
        </Field>

        {/* Ce que la phrase a compris, et qu'on peut corriger à la main : trois contrôles natifs. */}
        {/* La date dit « Fri (02/10/26) » : en tiers égaux elle était tronquée. */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1.35fr_1fr] gap-3">
          <Field label="Project">
            <select
              className="saisie"
              value={resolvedProject?.projectId ?? ''}
              onChange={(e) => setManualProjectId(e.target.value)}
              // Un #projet tapé dans la phrase fait foi : le corriger se fait dans la phrase.
              disabled={Boolean(parsedProject) || projects.length === 0}
            >
              {projects.length === 0 && <option value="">none</option>}
              {projects.map((p) => (
                <option key={p.projectId} value={p.projectId}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Date" group>
            <DateField value={resolvedDate} onChange={setManualDueDate} label="Pick a date" />
          </Field>
          <Field label="Repeat">
            <RepeatDropdown
              value={value}
              rrule={parsed?.rrule ?? null}
              today={today}
              onChange={setValue}
            />
          </Field>
        </div>

        <p className="alerte alerte-erreur" role="alert">
          {error}
        </p>
      </div>
    </Modal>
  );
}
